import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Button from "./Button";
import { ICONS } from "../../icons";
import { notify } from "../../utils/notify";

// ImportExportBar — a small "Export" + "Import" button pair, reused on the
// Categories/Products/Services list pages (the same CSV bulk feature that
// already exists for Translations — see Translations.js's own
// handleExport/handleImport — just without a language picker, since these
// three entities don't have one).
//
// `onExport` must return a Blob (see exportCategories/exportProducts/
// exportServices in services/api.js — same "own fetch, not the shared JSON
// request()" pattern as exportTranslations, since the response is a file).
// `onImport(file)` must return the backend's {created, updated, skipped,
// errors} summary (see ImportCategoriesHandler/ImportProductsHandler/
// ImportServicesHandler in Go) — shown inline in the modal so the admin can
// see exactly which rows didn't make it in and why, rather than a single
// opaque success/fail toast. `onImported` re-fetches the page's own list so
// newly added/updated rows show up immediately without a manual refresh.
export default function ImportExportBar({ entityLabel, fileBaseName, onExport, onImport, onImported }) {
  const [importOpen, setImportOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busyExport, setBusyExport] = useState(false);
  const [busyImport, setBusyImport] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const closeModal = () => {
    if (busyImport) return;
    setImportOpen(false);
    setFile(null);
    setResult(null);
  };

  const handleExport = async () => {
    setBusyExport(true);
    try {
      const blob = await onExport();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileBaseName}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      notify.success("Export ready");
    } catch (err) {
      notify.error(err.data?.error || err.message || `Failed to export ${entityLabel.toLowerCase()}`);
    } finally {
      setBusyExport(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setBusyImport(true);
    try {
      const summary = await onImport(file);
      setResult(summary);
      notify.success(`Added ${summary.created}, updated ${summary.updated}, skipped ${summary.skipped}`);
      onImported?.();
    } catch (err) {
      notify.error(err.data?.error || err.message || `Failed to import ${entityLabel.toLowerCase()}`);
    } finally {
      setBusyImport(false);
    }
  };

  return (
    <>
      <Button variant="outline" icon={ICONS.Download} onClick={handleExport} loading={busyExport}>
        Export
      </Button>
      <Button variant="outline" icon={ICONS.Install} onClick={() => setImportOpen(true)}>
        Import
      </Button>

      <AnimatePresence>
        {importOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 15 }}
              className="relative bg-white rounded-[2.5rem] p-8 max-w-lg w-full shadow-2xl z-10"
            >
              <h3 className="text-xl font-black text-gray-900 mb-1">Import {entityLabel}</h3>
              <p className="text-gray-400 text-sm mb-6">
                Upload a CSV built by Export (or matching its own columns). A row whose "id" matches an
                existing one updates it; a genuinely new row is added — nothing already saved is removed.
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full text-sm text-gray-600 bg-gray-50/50 rounded-xl border-2 border-dashed border-gray-200 hover:border-[var(--primary-color)] transition-all px-4 py-3 text-start"
              >
                {file ? file.name : "Choose a CSV file…"}
              </button>

              {result && (
                <div className="mt-4 text-sm bg-gray-50 rounded-2xl p-4 space-y-2 max-h-52 overflow-y-auto">
                  <p className="font-bold text-gray-700">
                    Added {result.created}, updated {result.updated}, skipped {result.skipped}
                  </p>
                  {result.errors?.length > 0 && (
                    <ul className="space-y-1 text-red-500 text-xs">
                      {result.errors.map((e, i) => (
                        <li key={i}>Row {e.row}: {e.message}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="flex gap-3 mt-8">
                <Button variant="secondary" onClick={closeModal} className="flex-1 !rounded-2xl" disabled={busyImport}>
                  Close
                </Button>
                <Button
                  variant="primary"
                  onClick={handleImport}
                  loading={busyImport}
                  className="flex-1 !rounded-2xl"
                  disabled={!file}
                >
                  Import
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
